import React, { useState, useEffect } from 'react';
import { LessonService, LessonSummary, GenericLesson } from '../../services/lessonServiceTypes';
import './GenericPastLessons.css';

interface GenericPastLessonsProps<T extends GenericLesson> {
  service: LessonService<T>;
  onSelectLesson: (lesson: T) => void;
  onClose: () => void;
  title?: string;
}

function GenericPastLessons<T extends GenericLesson>({
  service,
  onSelectLesson,
  onClose,
  title = 'Past Lessons'
}: GenericPastLessonsProps<T>) {
  const [lessons, setLessons] = useState<LessonSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  // Check if service supports level badges
  const hasLevelSupport = typeof service.extractLevelFromObjective === 'function' &&
                          typeof service.getLevelDisplayName === 'function';

  useEffect(() => {
    loadLessons();
  }, []);

  const loadLessons = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await service.getPastLessons(100);
      setLessons(result.lessons);
    } catch (err) {
      console.error('Error loading past lessons:', err);
      setError(err instanceof Error ? err.message : 'Failed to load lessons');
    } finally {
      setLoading(false);
    }
  };

  const handleLessonClick = async (lessonId: string) => {
    try {
      const lesson = await service.getLessonById(lessonId);
      onSelectLesson(lesson);
    } catch (err) {
      console.error('Error loading lesson:', err);
      setError(err instanceof Error ? err.message : 'Failed to load lesson');
    }
  };

  const handleDeleteClick = (lessonId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeleteConfirm(lessonId);
  };

  const handleConfirmDelete = async (lessonId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await service.deleteLesson(lessonId);
      setLessons(lessons.filter(l => l.lesson_id !== lessonId));
      setDeleteConfirm(null);
    } catch (err) {
      console.error('Error deleting lesson:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete lesson');
    }
  };

  const handleCancelDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDeleteConfirm(null);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const filteredLessons = lessons.filter(lesson =>
    lesson.lesson_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    lesson.lesson_objective.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const renderLessonCard = (lesson: LessonSummary) => {
    // Extract level if service supports it
    let level: string | null = null;
    let objectiveWithoutLevel = lesson.lesson_objective;

    if (hasLevelSupport && service.extractLevelFromObjective) {
      level = service.extractLevelFromObjective(lesson.lesson_objective);
      // Remove level prefix from objective for display
      objectiveWithoutLevel = lesson.lesson_objective.replace(/\[Level:\s*\w+\]\s*/, '');
    }

    return (
      <div
        key={lesson.lesson_id}
        className="past-lesson-card"
        onClick={() => handleLessonClick(lesson.lesson_id)}
      >
        <div className="past-lesson-content">
          {hasLevelSupport && level ? (
            <div className="past-lesson-header">
              <div className="past-lesson-name">{lesson.lesson_name}</div>
              <span className={`level-badge level-${level}`}>
                {service.getLevelDisplayName!(level)}
              </span>
            </div>
          ) : (
            <div className="past-lesson-name">{lesson.lesson_name}</div>
          )}
          <div className="past-lesson-objective">
            {truncateText(objectiveWithoutLevel, 120)}
          </div>
          <div className="past-lesson-date">
            {formatDate(lesson.created_at)}
          </div>
        </div>
        <div className="past-lesson-actions">
          {deleteConfirm === lesson.lesson_id ? (
            <div className="past-lesson-delete-confirm">
              <button
                className="past-lesson-confirm-btn"
                onClick={(e) => handleConfirmDelete(lesson.lesson_id, e)}
              >
                Delete
              </button>
              <button
                className="past-lesson-cancel-btn"
                onClick={handleCancelDelete}
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              className="past-lesson-delete-btn"
              onClick={(e) => handleDeleteClick(lesson.lesson_id, e)}
              aria-label="Delete lesson"
            >
              ×
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="past-lessons-container">
      <div className="past-lessons-header">
        <button 
          className="past-lessons-back-btn"
          onClick={onClose}
          aria-label="Back"
        >
          ←
        </button>
        <h2 className="past-lessons-title">{title}</h2>
      </div>

      <div className="past-lessons-search">
        <input
          type="text"
          className="past-lessons-search-input"
          placeholder="Search lessons..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      <div className="past-lessons-content">
        {loading && (
          <div className="past-lessons-loading">
            <div className="past-lessons-spinner"></div>
            <p>Loading lessons...</p>
          </div>
        )}

        {error && !loading && (
          <div className="past-lessons-error">
            <p>{error}</p>
            <button 
              className="past-lessons-retry-btn"
              onClick={loadLessons}
            >
              Try Again
            </button>
          </div>
        )}

        {!loading && !error && filteredLessons.length === 0 && searchQuery && (
          <div className="past-lessons-empty">
            <p>No lessons found matching "{searchQuery}"</p>
          </div>
        )}

        {!loading && !error && lessons.length === 0 && !searchQuery && (
          <div className="past-lessons-empty">
            <p>No past lessons yet. Generate your first lesson to get started!</p>
          </div>
        )}

        {!loading && !error && filteredLessons.length > 0 && (
          <div className="past-lessons-list">
            {filteredLessons.map(renderLessonCard)}
          </div>
        )}
      </div>
    </div>
  );
}

export default GenericPastLessons;

